import { useState, useMemo, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMsal } from '@azure/msal-react';
import { equipmentApi, usersApi, lookupsApi, type EquipmentRate, type EquipmentProjectOption } from '@/lib/apiClient';
import { useFocusTrap } from '@/lib/hooks/useFocusTrap';
import { fmtDate, fmtDateTime, toDateInputValue } from '@/lib/dates';
import {
  isCompleted, isOnSite, isCalledOff, endDateStatus, DUE_COLOR,
} from './equipmentStatus';
import { RatePicker } from './RatePicker';
import EquipmentRequestForm from './EquipmentRequestForm';
import EquipmentFiles from './EquipmentFiles';
import { canSeeRentalTerms, rateForSave } from './rentalTermsAccess';
import { exportEquipmentToXlsx } from './equipmentExport';
import Combobox, { type ComboboxOption } from '@/components/Combobox';

// ─── Types ────────────────────────────────────────────────────────────────────

/** `code` and `phase` are what behaviour branches on; `label` is display text
 *  that gets reworded; `sortOrder` is the lifecycle position, carried here so
 *  the grid needs no copy of the order (#306). */
interface EquipmentStatus {
  id: number;
  code: string;
  label: string;
  phase: 'Active' | 'Closed';
  sortOrder: number;
}
interface RentalType      { id: number; label: string }
interface BillingCycle    { id: number; label: string }
type ProjectRef = EquipmentProjectOption;

export interface Equipment {
  id: number;
  title: string;
  projectId: number | null;
  jobNameRaw: string | null;
  equipmentType: string | null;
  description: string | null;
  status: EquipmentStatus;
  deliveryDate: string | null;
  /** When the crew needs it, if later than delivery. Null on the ~340 requests
   *  that predate the field, and on any where the two are the same day. */
  startDate: string | null;
  endDate: string | null;
  /** Units requested. `rate` is PER UNIT. Always at least 1. */
  quantity: number;
  rentalType: RentalType | null;
  vendor: string | null;
  vendorContactInfo: string | null;
  poNumber: string | null;
  returnPoNumber: string | null;
  rate: number | null;
  billingCycle: BillingCycle | null;
  duration: number | null;
  requestedBy: string | null;
  ownedEquipmentNumber: string | null;
  notes: string | null;
  lastInspectionDate: string | null;
  deliverySpecifics: string | null;
  driverInstructions: string | null;
  altDeliveryAddress: string | null;
  costCode: string | null;
  attachmentDescription: string | null;
  project: ProjectRef | null;
  /**
   * When the call-off actually happened (#320). A real INSTANT, not a calendar
   * date — render with fmtDateTime, never fmtDate.
   *
   * Null for items never called off, and for those called off before
   * 2026-06-24, which is as far back as the activity log the backfill read
   * goes. Blank is the honest answer there: we do not know when it happened.
   */
  calledOffAt: string | null;
  createdAt: string;
  updatedAt: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

/**
 * Status presentation keys on the status CODE, never the label (#306).
 *
 * These were label-keyed, alongside a hard-coded id+label option list and a
 * hard-coded sort array — which made renaming a status a breaking change. When
 * 'On Rent' became 'On-site' the badge would have lost its colour, the row its
 * place in the sort, and worst, the Call Off and Extend buttons tested the
 * label and would have silently stopped rendering. Codes are stable; labels are
 * display-only and come from the API.
 */
const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  requested:  { bg: '#E1EEF7', text: '#0D4C7C' }, // ew-quality     — pending
  ordered:    { bg: '#FBECDC', text: '#8A4515' }, // ew-performance — ordered, en route
  on_site:    { bg: '#E6F2EB', text: '#015A28' }, // ew-green       — on the job
  called_off: { bg: '#FBE4DF', text: '#8F2414' }, // ew-danger      — called off
  complete:   { bg: '#EDEDEC', text: '#454543' }, // ew-neutral     — done
  canceled:   { bg: '#EDEDEC', text: '#63635F' }, // ew-neutral     — canceled
  approved:   { bg: '#E6F2EB', text: '#015A28' }, // retired — old rows still render
};

const RENTAL_OPTIONS  = [{ id: 1, label: 'Rental' }, { id: 2, label: 'Owned' }];
const BILLING_OPTIONS = [{ id: 1, label: 'Daily' }, { id: 2, label: 'Weekly' }, { id: 3, label: 'Monthly' }];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtCurrency(val: number | null | undefined) {
  if (val == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(val);
}

/**
 * Statuses straight from the database, in lifecycle order. Read rather than
 * hard-coded so adding or retiring one is a migration, not a release — the old
 * literal list carried ids AND labels, so it had to be edited in lockstep with
 * any change to the table (#306). Cached for the session; the table changes
 * about twice a year.
 */
function useEquipmentStatuses() {
  const { data = [] } = useQuery({
    queryKey: ['equipment-statuses'],
    queryFn: () => lookupsApi.listEquipmentStatuses(),
    staleTime: 60 * 60 * 1000,
  });
  return data;
}


// ─── Status Badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: { code?: string; label?: string } }) {
  const c = STATUS_COLORS[status.code ?? ''] ?? { bg: '#f3f4f6', text: '#6b7280' };
  const label = status.label ?? '—';
  return (
    <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 'var(--ew-r-sm)', fontSize: 11, fontWeight: 600, background: c.bg, color: c.text, letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
}

// ─── Sort ─────────────────────────────────────────────────────────────────────

type SortKey = 'type' | 'status' | 'vendor' | 'delivery' | 'endDate' | 'rentalType' | 'owned' | 'requestedBy' | 'submitted';
type SortDir = 'asc' | 'desc';

// Workflow order, not alphabetical — new requests first, finished work last.
// The order is the status row's own sort_order, carried on every request, so
// there is no client-side copy of the sequence to drift out of step with the
// database. An unrecognised status sorts last rather than first.
const statusRank = (sortOrder: number | null | undefined) => {
  return sortOrder ?? Number.MAX_SAFE_INTEGER;
};

function compareEquipment(a: Equipment, b: Equipment, key: SortKey, dir: SortDir): number {
  const f = dir === 'asc' ? 1 : -1;
  if (key === 'status') return f * (statusRank(a.status?.sortOrder) - statusRank(b.status?.sortOrder));
  const s = (v: string | null | undefined) => v ?? '';
  let av: string, bv: string;
  switch (key) {
    case 'type':        av = s(a.equipmentType);      bv = s(b.equipmentType);      break;
    case 'vendor':      av = s(a.vendor);             bv = s(b.vendor);             break;
    case 'delivery':    av = s(a.deliveryDate);       bv = s(b.deliveryDate);       break;
    case 'endDate':     av = s(a.endDate);            bv = s(b.endDate);            break;
    case 'rentalType':  av = s(a.rentalType?.label);  bv = s(b.rentalType?.label);  break;
    case 'owned':       av = s(a.ownedEquipmentNumber); bv = s(b.ownedEquipmentNumber); break;
    case 'requestedBy': av = s(a.requestedBy);        bv = s(b.requestedBy);        break;
    // createdAt is an ISO timestamp → lexicographic compare orders chronologically.
    case 'submitted':   av = s(a.createdAt);          bv = s(b.createdAt);          break;
    default: return 0;
  }
  return f * av.localeCompare(bv);
}

interface EquipmentGroup {
  key: string;
  jobNumber: string;
  label: string;
  items: Equipment[];
}

function groupEquipment(rows: Equipment[], key: SortKey, dir: SortDir): EquipmentGroup[] {
  // Sort within each group
  const sorted = [...rows].sort((a, b) => compareEquipment(a, b, key, dir));

  // Group by project
  const map = new Map<string, EquipmentGroup>();
  for (const item of sorted) {
    const gkey = item.project ? `p_${item.project.id}` : `r_${item.jobNameRaw ?? ''}`;
    if (!map.has(gkey)) {
      map.set(gkey, {
        key: gkey,
        jobNumber: item.project?.jobNumber ?? item.jobNameRaw ?? '',
        label: item.project ? item.project.title : (item.jobNameRaw ?? 'No Project'),
        items: [],
      });
    }
    map.get(gkey)!.items.push(item);
  }

  // Order groups by the active sort too — each group's first item is its
  // extreme for the current column, so groups with the "top" values rise.
  // Without this, group order never changed and header clicks looked dead.
  return [...map.values()].sort((a, b) =>
    compareEquipment(a.items[0], b.items[0], key, dir) || a.jobNumber.localeCompare(b.jobNumber)
  );
}

function Th({ label, sortKey, current, dir, onSort }: {
  label: string; sortKey: SortKey; current: SortKey; dir: SortDir; onSort: (k: SortKey) => void;
}) {
  const active = current === sortKey;
  return (
    <th onClick={() => onSort(sortKey)} style={{
      padding: '8px 12px', textAlign: 'left', whiteSpace: 'nowrap',
      fontWeight: 600, fontSize: 12, color: active ? 'var(--ew)' : 'var(--gray)',
      cursor: 'pointer', userSelect: 'none', borderBottom: '2px solid var(--border)', background: 'var(--surface)',
    }}>
      {label} <span style={{ opacity: active ? 1 : 0.3 }}>{active ? (dir === 'asc' ? '↑' : '↓') : '↕'}</span>
    </th>
  );
}

// ─── Inline Detail ────────────────────────────────────────────────────────────

function DF({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 12, padding: '3px 0', borderBottom: '1px solid #f3f4f6' }}>
      <span style={{ color: '#6b7280', flexShrink: 0, marginRight: 8 }}>{label}</span>
      <span style={{ fontWeight: 500, textAlign: 'right', color: value ? '#1f2937' : '#9ca3af' }}>{value ?? '—'}</span>
    </div>
  );
}

// Compact read-only label/value row used by the request form to surface
// project-derived info (field leader, job address, site contact) that isn't
// itself an editable equipment field.
function ReadOnlyRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 8, fontSize: 12, lineHeight: 1.4 }}>
      <span style={{ color: 'var(--ew-text-subtle, #6B7280)', flexShrink: 0, minWidth: 86 }}>{label}</span>
      <span style={{ fontWeight: 500, color: 'var(--ew-text, #1F2937)' }}>{value}</span>
    </div>
  );
}

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--ew-r-lg)', padding: '14px 16px' }}>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.8px', color: '#6b7280', marginBottom: 10 }}>{title}</div>
      {children}
    </div>
  );
}


export function InlineDetail({ item, projects, onClose, onEdit, canEdit, showRate }: { item: Equipment; projects: ProjectRef[]; onClose: () => void; onEdit: () => void; canEdit: boolean; showRate: boolean }) {
  // The embedded item.project only carries id/jobNumber/title. Recover the job
  // address by matching the linked job number against the project-options list,
  // the same way the request form prefills it on a new equipment request.
  const jobAddress = item.project
    ? projects.find(p => p.jobNumber.trim() === item.project!.jobNumber.trim())?.jobAddress ?? null
    : null;
  return (
    <div style={{ padding: '16px 20px', background: '#f9fafb', borderTop: '2px solid var(--ew)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <StatusBadge status={item.status} />
          {item.rentalType && <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>{item.rentalType.label}</span>}
          {item.equipmentType && <span style={{ fontSize: 11, background: '#f0fdf4', color: 'var(--ew)', border: '1px solid #bbf7d0', borderRadius: 'var(--ew-r-lg)', padding: '1px 8px', fontWeight: 600 }}>{item.equipmentType}</span>}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {canEdit && <button onClick={onEdit} style={{ fontSize: 12, padding: '4px 12px', background: 'var(--ew)', color: '#fff', border: 'none', borderRadius: 'var(--ew-r-md)', cursor: 'pointer', fontWeight: 600 }}>Edit</button>}
          <button onClick={onClose} style={{ fontSize: 12, padding: '4px 10px', border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', background: '#fff', cursor: 'pointer', color: '#6b7280' }}>Close</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, marginBottom: 12 }}>
        <DetailSection title="Project">
          <DF label="Project" value={item.project ? `#${item.project.jobNumber} — ${item.project.title}` : item.jobNameRaw} />
          {jobAddress && <DF label="Job Address" value={jobAddress} />}
          <DF label="Requested By" value={item.requestedBy} />
          <DF label="Cost Code" value={item.costCode} />
          {item.description && <DF label="Description" value={item.description} />}
          {item.attachmentDescription && <DF label="Attachments" value={item.attachmentDescription} />}
        </DetailSection>

        <DetailSection title="Dates & Duration">
          <DF label="Delivery Date" value={fmtDate(item.deliveryDate)} />
          {/* Only shown when it differs from delivery — on most requests the
              gear is dropped and started the same day, and a duplicate date
              adds a line without adding information. */}
          {item.startDate && item.startDate !== item.deliveryDate && (
            <DF label="Start Date" value={fmtDate(item.startDate)} />
          )}
          <DF label="End Date" value={fmtDate(item.endDate)} />
          {/* Only once it has actually been called off — an empty row on every
              live rental would be noise. fmtDateTime, not fmtDate: this one is
              a real instant. */}
          {item.calledOffAt && (
            <DF label="Called Off" value={fmtDateTime(item.calledOffAt)} />
          )}
          <DF label="Duration" value={item.duration ? `${item.duration} ${item.billingCycle?.label ?? ''}` : null} />
          <DF label="Last Inspection" value={fmtDate(item.lastInspectionDate)} />
          <DF label="Submitted" value={fmtDateTime(item.createdAt)} />
        </DetailSection>

        <DetailSection title="Vendor & Billing">
          <DF label="Vendor" value={item.vendor} />
          <DF label="Contact" value={item.vendorContactInfo} />
          {/* "/ unit" only once quantity is above 1 — spelling it out on every
              single-unit request would be noise, but where it matters the rate
              must not read as the total for all of them. */}
          {/* Only for equipment editors (#571, rentalTermsAccess.ts): everyone else gets a null rate
              from the API, and a "Rate —" row would read as "no rate set". */}
          {showRate && <DF label="Rate" value={item.rate != null
            ? `${fmtCurrency(item.rate)} / ${item.billingCycle?.label ?? ''}${(item.quantity ?? 1) > 1 ? ' / unit' : ''}`
            : null} />}
          {(item.quantity ?? 1) > 1 && <DF label="Quantity" value={String(item.quantity)} />}
          <DF label="PO #" value={item.poNumber} />
          <DF label="Return PO #" value={item.returnPoNumber} />
          {item.ownedEquipmentNumber && <DF label="Owned Eq #" value={item.ownedEquipmentNumber} />}
        </DetailSection>
      </div>

      {(item.deliverySpecifics || item.driverInstructions || item.altDeliveryAddress) && (
        <DetailSection title="Delivery Details">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
            {item.altDeliveryAddress && <DF label="Alt. Address" value={item.altDeliveryAddress} />}
            {item.deliverySpecifics  && <DF label="Specifics"   value={item.deliverySpecifics} />}
            {item.driverInstructions && <DF label="Driver Instr." value={item.driverInstructions} />}
          </div>
        </DetailSection>
      )}

      {item.notes && (
        <div style={{ marginTop: 12 }}>
          <DetailSection title="Notes">
            <p style={{ fontSize: 12, margin: 0, whiteSpace: 'pre-wrap', color: '#1f2937' }}>{item.notes}</p>
          </DetailSection>
        </div>
      )}
    </div>
  );
}

// ─── Rate Calculator Modal ────────────────────────────────────────────────────

function RateCalculatorModal({ onClose }: { onClose: () => void }) {
  const modalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalRef, { onEscape: onClose });
  const [pickedRate, setPickedRate] = useState<EquipmentRate | null>(null);
  const [equipmentType, setEquipmentType] = useState('');
  const [days, setDays] = useState('7');

  const numDays = Math.max(0, Number.parseInt(days, 10) || 0);

  // Standard rental conventions: 7 days per week, 28 days per month.
  // We round UP to the next whole unit since rental billing typically does.
  const estimates = useMemo(() => {
    if (!pickedRate || numDays <= 0) return null;
    const dailyTotal   = pickedRate.dayRate   != null ? pickedRate.dayRate   * numDays              : null;
    const weeklyUnits  = Math.ceil(numDays / 7);
    const weeklyTotal  = pickedRate.weekRate  != null ? pickedRate.weekRate  * weeklyUnits          : null;
    const monthlyUnits = Math.ceil(numDays / 28);
    const monthlyTotal = pickedRate.monthRate != null ? pickedRate.monthRate * monthlyUnits         : null;
    const candidates = [
      { key: 'daily',   label: 'Daily',   units: numDays,      unitLabel: numDays === 1 ? 'day' : 'days',         rate: pickedRate.dayRate,   total: dailyTotal },
      { key: 'weekly',  label: 'Weekly',  units: weeklyUnits,  unitLabel: weeklyUnits === 1 ? 'week' : 'weeks',   rate: pickedRate.weekRate,  total: weeklyTotal },
      { key: 'monthly', label: 'Monthly', units: monthlyUnits, unitLabel: monthlyUnits === 1 ? 'month' : 'months', rate: pickedRate.monthRate, total: monthlyTotal },
    ];
    const valid = candidates.filter((c): c is typeof c & { total: number } => c.total != null);
    const cheapestKey = valid.length ? valid.reduce((best, c) => c.total < best.total ? c : best).key : null;
    return { candidates, cheapestKey };
  }, [pickedRate, numDays]);

  const fmt = (n: number | null) => n != null
    ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 })
    : '—';

  const inp: React.CSSProperties = { border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', padding: '6px 10px', fontSize: 13, width: '100%', fontFamily: 'inherit', background: '#fff', color: 'var(--text)' };
  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--gray)', marginBottom: 3, display: 'block' };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div ref={modalRef} onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 'var(--ew-r-lg)', width: 'min(560px, 92vw)', maxHeight: '92vh', overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Rate Calculator</div>
            <div style={{ fontSize: 12, color: 'var(--gray)', marginTop: 2 }}>
              Estimate cost for a piece of equipment over a given duration.
            </div>
          </div>
          <button onClick={onClose} aria-label="Close rate calculator" style={{ fontSize: 18, color: 'var(--gray)', background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px' }}>✕</button>
        </div>

        <div style={{ padding: '16px 20px 4px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={lbl}>Equipment</label>
            <RatePicker
              value={equipmentType}
              selectedRate={pickedRate}
              onChange={({ equipmentType: et, rate }) => { setEquipmentType(et); setPickedRate(rate); }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', maxWidth: 200 }}>
            <label style={lbl}>Duration (days)</label>
            <input type="number" min={1} style={inp} value={days} onChange={e => setDays(e.target.value)} />
          </div>
        </div>

        <div style={{ padding: '12px 20px 20px' }}>
          {!pickedRate && (
            <div style={{ padding: 16, fontSize: 13, color: 'var(--gray)', textAlign: 'center', background: 'var(--surface)', borderRadius: 'var(--ew-r-md)', border: '1px dashed var(--border)' }}>
              Pick an equipment item above to see rate estimates.
            </div>
          )}
          {pickedRate && estimates && numDays > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.8px', color: 'var(--ew)', marginBottom: 8 }}>
                Estimates for {numDays} {numDays === 1 ? 'day' : 'days'}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {estimates.candidates.map((c) => {
                  const isCheapest = estimates.cheapestKey === c.key && c.total != null;
                  return (
                    <div key={c.key} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                      padding: '10px 12px', borderRadius: 'var(--ew-r-md)',
                      background: isCheapest ? 'var(--ew-dim, #f0fdf4)' : '#fff',
                      border: `1px solid ${isCheapest ? 'var(--ew)' : 'var(--border)'}`,
                    }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>
                          {c.label} billing
                          {isCheapest && <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, color: 'var(--ew)', background: '#fff', border: '1px solid var(--ew)', borderRadius: 'var(--ew-r-lg)', padding: '1px 8px' }}>CHEAPEST</span>}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 2 }}>
                          {c.units} {c.unitLabel} × {fmt(c.rate)}
                        </div>
                      </div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: c.total != null ? 'var(--text)' : 'var(--gray)' }}>
                        {fmt(c.total)}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: 10, fontSize: 11, color: 'var(--gray)', fontStyle: 'italic' }}>
                Estimates assume 7 days per week and 28 days per month, rounded up to the nearest whole billing unit. Confirm final pricing with the vendor.
              </div>
            </>
          )}
        </div>

        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8, background: 'var(--surface)' }}>
          <button onClick={onClose} style={{ fontSize: 13, color: 'var(--gray)', border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', padding: '6px 14px', background: '#fff', cursor: 'pointer' }}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── Edit Drawer ──────────────────────────────────────────────────────────────

interface EditDrawerProps {
  item: Equipment | null; // null = new
  projects: ProjectRef[];
  /** Equipment editors only (#571, rentalTermsAccess.ts): the rate field, and the files section. */
  canSeeRentalTerms: boolean;
  onClose: () => void;
  onSaved: () => void;
}

function EditDrawer({ item, projects, canSeeRentalTerms: showRentalTerms, onClose, onSaved }: EditDrawerProps) {
  const statuses = useEquipmentStatuses();
  // Offer the selectable statuses, plus whichever one this request is already
  // on. Without that second part, opening a request sitting on a retired
  // status would silently re-point it at the first option in the list on save.
  const editStatusOptions = useMemo(() => {
    const selectable = statuses.filter((s) => s.isSelectable);
    const current = statuses.find((s) => s.id === item?.status?.id);
    return current && !current.isSelectable ? [...selectable, current] : selectable;
  }, [statuses, item?.status?.id]);
  const qc = useQueryClient();
  const { accounts } = useMsal();
  const authorName = accounts[0]?.name ?? accounts[0]?.username ?? 'Unknown';
  const drawerRef = useRef<HTMLDivElement>(null);
  useFocusTrap(drawerRef, { onEscape: onClose });

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [newNote, setNewNote] = useState('');
  const [pickedRate, setPickedRate] = useState<EquipmentRate | null>(null);

  const [form, setForm] = useState<Record<string, string>>(() => ({
    title:                item?.title ?? '',
    projectId:            String(item?.projectId ?? ''),
    jobNameRaw:           item?.jobNameRaw ?? '',
    equipmentType:        item?.equipmentType ?? '',
    description:          item?.description ?? '',
    statusId:             String(item?.status?.id ?? 1),
    deliveryDate:         toDateInputValue(item?.deliveryDate),
    startDate:            toDateInputValue(item?.startDate),
    endDate:              toDateInputValue(item?.endDate),
    quantity:             String(item?.quantity ?? 1),
    rentalTypeId:         String(item?.rentalType?.id ?? ''),
    vendor:               item?.vendor ?? '',
    vendorContactInfo:    item?.vendorContactInfo ?? '',
    poNumber:             item?.poNumber ?? '',
    returnPoNumber:       item?.returnPoNumber ?? '',
    rate:                 String(item?.rate ?? ''),
    billingCycleId:       String(item?.billingCycle?.id ?? ''),
    duration:             String(item?.duration ?? ''),
    requestedBy:          item?.requestedBy ?? '',
    ownedEquipmentNumber: item?.ownedEquipmentNumber ?? '',
    lastInspectionDate:   toDateInputValue(item?.lastInspectionDate),
    deliverySpecifics:    item?.deliverySpecifics ?? '',
    driverInstructions:   item?.driverInstructions ?? '',
    altDeliveryAddress:   item?.altDeliveryAddress ?? '',
    costCode:             item?.costCode ?? '',
    attachmentDescription:item?.attachmentDescription ?? '',
  }));

  const f   = (k: string) => form[k] ?? '';
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(p => ({ ...p, [k]: e.target.value }));

  // Owned Equipment # picker source — active units from the Admin catalog.
  // Free typing is still allowed (Combobox keeps unmatched text) as a fallback.
  const { data: ownedUnits = [] } = useQuery({
    queryKey: ['equipment', 'owned-units'],
    queryFn: () => equipmentApi.ownedUnits(),
    staleTime: 300_000,
  });
  const ownedOptions: ComboboxOption[] = ownedUnits.map((u) => ({
    value: u.unitNumber,
    sub: [u.description, u.equipmentClass].filter(Boolean).join(' · ') || undefined,
  }));

  const handleSave = useCallback(async () => {
    setSaving(true); setSaveError(null);
    const ne = (v: string) => v === '' ? null : v;
    const ni = (v: string) => v === '' ? null : Number(v);

    let finalNotes = item?.notes ?? null;
    if (newNote.trim()) {
      const stamp = `[${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${authorName}]\n${newNote.trim()}`;
      finalNotes = finalNotes ? `${stamp}\n---\n${finalNotes}` : stamp;
    }

    const payload = {
      title: f('title') || undefined,
      projectId: f('projectId') ? Number(f('projectId')) : null,
      jobNameRaw: ne(f('jobNameRaw')),
      equipmentType: ne(f('equipmentType')),
      description: ne(f('description')),
      statusId: Number(f('statusId')) || 1,
      deliveryDate: ne(f('deliveryDate')),
      startDate: ne(f('startDate')),
      endDate: ne(f('endDate')),
      // Always sent, so clearing the box genuinely clears it and an untouched
      // one round-trips its current value rather than being dropped.
      quantity: Math.max(1, Number(f('quantity')) || 1),
      rentalTypeId: ni(f('rentalTypeId')),
      vendor: ne(f('vendor')),
      vendorContactInfo: ne(f('vendorContactInfo')),
      poNumber: ne(f('poNumber')),
      returnPoNumber: ne(f('returnPoNumber')),
      // Equipment editors only (#571) — see rateForSave.
      ...rateForSave(showRentalTerms, f('rate'), item?.rate),
      billingCycleId: ni(f('billingCycleId')),
      duration: ni(f('duration')),
      requestedBy: ne(f('requestedBy')),
      ownedEquipmentNumber: ne(f('ownedEquipmentNumber')),
      notes: finalNotes,
      lastInspectionDate: ne(f('lastInspectionDate')),
      deliverySpecifics: ne(f('deliverySpecifics')),
      driverInstructions: ne(f('driverInstructions')),
      altDeliveryAddress: ne(f('altDeliveryAddress')),
      costCode: ne(f('costCode')),
      attachmentDescription: ne(f('attachmentDescription')),
    };

    try {
      if (item) {
        await equipmentApi.update(item.id, payload);
      } else {
        await equipmentApi.create(payload);
      }
      qc.invalidateQueries({ queryKey: ['equipment'] });
      onSaved();
    } catch (err: any) {
      setSaveError(err.message);
    }
    setSaving(false);
  }, [form, newNote, authorName, item, onSaved, qc, showRentalTerms]);

  const deleteMutation = useMutation({
    mutationFn: () => equipmentApi.delete(item!.id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['equipment'] }); onClose(); },
  });

  const inp: React.CSSProperties = { border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', padding: '6px 10px', fontSize: 13, width: '100%', fontFamily: 'inherit', background: '#fff', color: 'var(--text)' };
  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '.5px', color: 'var(--gray)', marginBottom: 3, display: 'block' };
  const fw: React.CSSProperties  = { display: 'flex', flexDirection: 'column' as const };
  const g2: React.CSSProperties  = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 14px' };
  const sh = (title: string) => (
    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '.8px', color: 'var(--ew)', borderBottom: '1px solid var(--border)', paddingBottom: 6, marginBottom: 10, marginTop: 18 }}>
      {title}
    </div>
  );

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 200 }} />
      <div ref={drawerRef} style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 620, background: '#fff', zIndex: 201, overflowY: 'auto', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{item ? item.title : 'New Equipment Request'}</div>
            {item?.project && <div style={{ fontSize: 12, color: 'var(--ew)', fontWeight: 600 }}>#{item.project.jobNumber}</div>}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {item && (
              <button onClick={() => { if (confirm('Delete this equipment request?')) deleteMutation.mutate(); }}
                style={{ fontSize: 12, color: '#b91c1c', border: '1px solid #fca5a5', borderRadius: 'var(--ew-r-md)', padding: '5px 10px', background: '#fff', cursor: 'pointer' }}>
                Delete
              </button>
            )}
            <button onClick={onClose} style={{ fontSize: 12, color: 'var(--gray)', border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', padding: '5px 10px', background: '#fff', cursor: 'pointer' }}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={{ fontSize: 12, background: 'var(--ew)', color: '#fff', border: 'none', borderRadius: 'var(--ew-r-md)', padding: '6px 14px', cursor: 'pointer', fontWeight: 600, opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button onClick={onClose} aria-label="Close edit drawer" style={{ fontSize: 18, color: 'var(--gray)', background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px' }}>✕</button>
          </div>
        </div>

        {saveError && <div style={{ margin: '8px 18px 0', padding: '8px 12px', background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', fontSize: 12 }}>{saveError}</div>}

        <div style={{ padding: '4px 18px 32px' }}>
          {sh('Equipment')}
          <div style={g2}>
            <div style={{ ...fw, gridColumn: '1/-1' }}>
              <label style={lbl}>Equipment Type</label>
              <RatePicker
                value={f('equipmentType')}
                selectedRate={pickedRate}
                onChange={({ equipmentType: et, rate }) => {
                  setPickedRate(rate);
                  setForm(p => ({ ...p, equipmentType: et }));
                }}
              />
              {showRentalTerms && pickedRate && f('billingCycleId') && (() => {
                const cycleId = Number(f('billingCycleId'));
                const cycleLabel = BILLING_OPTIONS.find(b => b.id === cycleId)?.label;
                const rateForCycle =
                  cycleLabel === 'Daily'   ? pickedRate.dayRate :
                  cycleLabel === 'Weekly'  ? pickedRate.weekRate :
                  cycleLabel === 'Monthly' ? pickedRate.monthRate : null;
                if (rateForCycle == null) return null;
                const currentRate = parseFloat(f('rate'));
                if (currentRate === rateForCycle) return null;
                return (
                  <button
                    type="button"
                    onClick={() => setForm(p => ({ ...p, rate: String(rateForCycle) }))}
                    style={{ alignSelf: 'flex-start', marginTop: 6, fontSize: 11, padding: '3px 10px', border: '1px solid var(--ew)', borderRadius: 'var(--ew-r-md)', background: '#fff', color: 'var(--ew)', cursor: 'pointer', fontWeight: 600 }}
                  >
                    Apply {cycleLabel} rate (${rateForCycle.toLocaleString('en-US')})
                  </button>
                );
              })()}
            </div>
            <div style={{ ...fw, gridColumn: '1/-1' }}><label style={lbl}>Title / Name</label><input style={inp} value={f('title')} onChange={set('title')} /></div>
            <div style={fw}><label style={lbl}>Status</label>
              <select style={inp} value={f('statusId')} onChange={set('statusId')}>
                {/* Retired statuses are filtered out here — a request must not
                    be SET to one — but a request already holding one keeps
                    showing it, because the option is added back below. */}
                {editStatusOptions.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
            <div style={fw}><label style={lbl}>Rental / Owned</label>
              <select style={inp} value={f('rentalTypeId')} onChange={set('rentalTypeId')}>
                <option value="">— Select —</option>
                {RENTAL_OPTIONS.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
            </div>
            <div style={fw}><label style={lbl}>Owned Equipment #</label>
              <Combobox
                value={f('ownedEquipmentNumber')}
                onChange={(v) => setForm(p => ({ ...p, ownedEquipmentNumber: v }))}
                options={ownedOptions}
                placeholder="Select or type a unit #"
              />
            </div>
            <div style={{ ...fw, gridColumn: '1/-1' }}><label style={lbl}>Description</label><textarea style={{ ...inp, resize: 'vertical', minHeight: 48 }} value={f('description')} onChange={set('description')} /></div>
            <div style={{ ...fw, gridColumn: '1/-1' }}><label style={lbl}>Attachments / Accessories</label><textarea style={{ ...inp, resize: 'vertical', minHeight: 40 }} value={f('attachmentDescription')} onChange={set('attachmentDescription')} /></div>
          </div>

          {sh('Project')}
          <div style={g2}>
            <div style={fw}><label style={lbl}>Project</label>
              <select style={inp} value={f('projectId')} onChange={set('projectId')}>
                <option value="">— No project linked —</option>
                {projects.map(p => <option key={p.id} value={p.id}>#{p.jobNumber} — {p.title}</option>)}
              </select>
            </div>
            <div style={fw}><label style={lbl}>Job Name (if unlinked)</label><input style={inp} value={f('jobNameRaw')} onChange={set('jobNameRaw')} placeholder="Only needed if no project selected" /></div>
            <div style={fw}><label style={lbl}>Requested By</label><input style={inp} value={f('requestedBy')} onChange={set('requestedBy')} /></div>
            <div style={fw}><label style={lbl}>Cost Code</label><input style={inp} value={f('costCode')} onChange={set('costCode')} /></div>
          </div>

          {sh('Schedule')}
          <div style={g2}>
            <div style={fw}><label style={lbl}>Delivery Date</label><input type="date" style={inp} value={f('deliveryDate')} onChange={set('deliveryDate')} /></div>
            <div style={fw}><label style={lbl}>Start Date</label><input type="date" style={inp} value={f('startDate')} onChange={set('startDate')}
              title="When the crew actually needs it, if later than delivery" /></div>
            <div style={fw}><label style={lbl}>End Date</label><input type="date" style={inp} value={f('endDate')} onChange={set('endDate')} /></div>
            <div style={fw}><label style={lbl}>Quantity</label><input type="number" min={1} step={1} style={inp} value={f('quantity')} onChange={set('quantity')}
              title="How many units. The rate is per unit." /></div>
            <div style={fw}><label style={lbl}>Last Inspection Date</label><input type="date" style={inp} value={f('lastInspectionDate')} onChange={set('lastInspectionDate')} /></div>
          </div>

          {sh('Vendor & Billing')}
          <div style={g2}>
            <div style={fw}><label style={lbl}>Vendor</label><input style={inp} value={f('vendor')} onChange={set('vendor')} /></div>
            <div style={fw}><label style={lbl}>Vendor Contact</label><input style={inp} value={f('vendorContactInfo')} onChange={set('vendorContactInfo')} /></div>
            {showRentalTerms && (
              <div style={fw}><label style={lbl}>Rate</label><input type="number" style={inp} value={f('rate')} onChange={set('rate')} min={0} step={0.01} /></div>
            )}
            <div style={fw}><label style={lbl}>Billing Cycle</label>
              <select style={inp} value={f('billingCycleId')} onChange={set('billingCycleId')}>
                <option value="">— Select —</option>
                {BILLING_OPTIONS.map(b => <option key={b.id} value={b.id}>{b.label}</option>)}
              </select>
            </div>
            <div style={fw}><label style={lbl}>Duration</label><input type="number" style={inp} value={f('duration')} onChange={set('duration')} min={0} /></div>
            <div style={fw}><label style={lbl}>PO #</label><input style={inp} value={f('poNumber')} onChange={set('poNumber')} /></div>
            <div style={fw}><label style={lbl}>Return PO #</label><input style={inp} value={f('returnPoNumber')} onChange={set('returnPoNumber')} /></div>
          </div>

          {/* #571 — the rental agreement and other files. Equipment editors only,
              and only on a saved request: a new one has no id to attach to. */}
          {item && showRentalTerms && (
            <>
              {sh('Rental Agreement & Files')}
              <EquipmentFiles equipmentId={item.id} />
            </>
          )}

          {sh('Delivery Details')}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={fw}><label style={lbl}>Alt. Delivery Address</label><input style={inp} value={f('altDeliveryAddress')} onChange={set('altDeliveryAddress')} /></div>
            <div style={fw}><label style={lbl}>Delivery Specifics</label><textarea style={{ ...inp, resize: 'vertical', minHeight: 48 }} value={f('deliverySpecifics')} onChange={set('deliverySpecifics')} /></div>
            <div style={fw}><label style={lbl}>Driver Instructions</label><textarea style={{ ...inp, resize: 'vertical', minHeight: 48 }} value={f('driverInstructions')} onChange={set('driverInstructions')} /></div>
          </div>

          {sh('Notes')}
          {item?.notes && (
            <div style={{ marginBottom: 8, maxHeight: 200, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', padding: '8px 10px', background: 'var(--surface)', fontSize: 12, whiteSpace: 'pre-wrap', color: '#1f2937' }}>
              {item.notes}
            </div>
          )}
          <div style={fw}>
            <label style={lbl}>Add Note</label>
            <textarea style={{ ...inp, resize: 'vertical', minHeight: 60 }} placeholder="Add a new note entry…" value={newNote} onChange={e => setNewNote(e.target.value)} />
            <div style={{ fontSize: 10, color: 'var(--gray)', marginTop: 3 }}>
              Will be saved as: {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} — {authorName}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Call Off Modal ───────────────────────────────────────────────────────────

type Condition = 'clean' | 'needs_cleaning' | 'damage';

export function CallOffModal({ item, projects, onClose, onSaved }: { item: Equipment; projects: ProjectRef[]; onClose: () => void; onSaved: () => void }) {
  const qc = useQueryClient();
  const modalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalRef, { onEscape: onClose });
  // Prefill from the linked project where available — the job site is the
  // natural pickup location and site contact. Fall back to the equipment's own
  // alt-delivery address / requester when the project has nothing on file.
  const project = item.projectId != null ? projects.find(p => p.id === item.projectId) : undefined;
  const [pickupReadyDate, setPickupReadyDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [pickupLocation,  setPickupLocation]  = useState(project?.jobAddress ?? item.altDeliveryAddress ?? '');
  const [siteContactName, setSiteContactName] = useState(project?.siteContactName ?? item.requestedBy ?? '');
  const [siteContactPhone,setSiteContactPhone]= useState(project?.siteContactPhone ?? '');
  const [condition, setCondition] = useState<Condition>('clean');
  const [damageDescription, setDamageDescription] = useState('');
  const [finalUsage, setFinalUsage] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    if (!pickupReadyDate)       { setError('Pickup ready date is required.'); return; }
    if (!pickupLocation.trim()) { setError('Pickup location is required.'); return; }
    if (!siteContactName.trim()){ setError('Site contact name is required.'); return; }
    if (!siteContactPhone.trim()){ setError('Site contact phone is required.'); return; }
    if (condition === 'damage' && !damageDescription.trim()) {
      setError('Damage description is required when reporting damage.'); return;
    }
    setSaving(true);
    try {
      await equipmentApi.callOff(item.id, {
        pickupReadyDate,
        pickupLocation:   pickupLocation.trim(),
        siteContactName:  siteContactName.trim(),
        siteContactPhone: siteContactPhone.trim(),
        condition,
        damageDescription: condition === 'damage' ? damageDescription.trim() : undefined,
        finalUsage:       finalUsage ? parseFloat(finalUsage) : undefined,
        additionalNotes:  additionalNotes.trim() || undefined,
      });
      qc.invalidateQueries({ queryKey: ['equipment'] });
      onSaved();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const inp: React.CSSProperties = { border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', padding: '6px 10px', fontSize: 13, width: '100%', fontFamily: 'inherit', background: '#fff', color: 'var(--text)' };
  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--gray)', marginBottom: 3, display: 'block' };
  const fw:  React.CSSProperties = { display: 'flex', flexDirection: 'column' };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div ref={modalRef} onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 'var(--ew-r-lg)', width: 'min(540px, 92vw)', maxHeight: '92vh', overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16 }}>Call Off Equipment</div>
              <div style={{ fontSize: 12, color: 'var(--gray)', marginTop: 2 }}>
                {item.equipmentType ?? item.title}{item.project ? ` · #${item.project.jobNumber}` : ''}
              </div>
            </div>
            <button onClick={onClose} aria-label="Close call off modal" style={{ fontSize: 18, color: 'var(--gray)', background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px' }}>✕</button>
          </div>

          {error && <div style={{ margin: '12px 20px 0', padding: '8px 12px', background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', fontSize: 12 }}>{error}</div>}

          <div style={{ padding: '16px 20px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {project && (project.fieldLeader || project.jobAddress || project.siteContactName) && (
              <div style={{ background: 'var(--ew-bg-subtle, #F6F7F9)', border: '1px solid var(--ew-border, #E2E5EA)', borderRadius: 'var(--ew-r-md)', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--ew-text-subtle, #6B7280)' }}>From project #{project.jobNumber} — prefilled below, edit if needed</div>
                {project.fieldLeader && <ReadOnlyRow label="Field Leader" value={project.fieldLeader} />}
              </div>
            )}
            <div style={fw}>
              <label style={lbl}>Pickup Ready Date <span style={{ color: '#b91c1c' }}>*</span></label>
              <input type="date" style={inp} value={pickupReadyDate} onChange={e => setPickupReadyDate(e.target.value)} />
            </div>
            <div style={fw}>
              <label style={lbl}>Pickup Location <span style={{ color: '#b91c1c' }}>*</span></label>
              <input style={inp} value={pickupLocation} onChange={e => setPickupLocation(e.target.value)} placeholder="Site address or where to find the equipment" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={fw}>
                <label style={lbl}>Site Contact Name <span style={{ color: '#b91c1c' }}>*</span></label>
                <input style={inp} value={siteContactName} onChange={e => setSiteContactName(e.target.value)} />
              </div>
              <div style={fw}>
                <label style={lbl}>Site Contact Phone <span style={{ color: '#b91c1c' }}>*</span></label>
                <input style={inp} value={siteContactPhone} onChange={e => setSiteContactPhone(e.target.value)} placeholder="e.g. 555-123-4567" />
              </div>
            </div>
            <div style={fw}>
              <label style={lbl}>Equipment Condition <span style={{ color: '#b91c1c' }}>*</span></label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
                {([
                  { v: 'clean',          label: 'Clean & ready for pickup' },
                  { v: 'needs_cleaning', label: 'Needs cleaning' },
                  { v: 'damage',         label: 'Damage to report' },
                ] as { v: Condition; label: string }[]).map(opt => (
                  <label key={opt.v} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="radio" name="condition" checked={condition === opt.v} onChange={() => setCondition(opt.v)} />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>
            {condition === 'damage' && (
              <div style={fw}>
                <label style={lbl}>Damage Description <span style={{ color: '#b91c1c' }}>*</span></label>
                <textarea style={{ ...inp, resize: 'vertical', minHeight: 60 }} value={damageDescription} onChange={e => setDamageDescription(e.target.value)} placeholder="Describe the damage so ops can document it before pickup" />
              </div>
            )}
            <div style={fw}>
              <label style={lbl}>Final Hours / Usage Reading (optional)</label>
              <input type="number" min={0} step="0.1" style={inp} value={finalUsage} onChange={e => setFinalUsage(e.target.value)} />
            </div>
            <div style={fw}>
              <label style={lbl}>Additional Notes (optional)</label>
              <textarea style={{ ...inp, resize: 'vertical', minHeight: 50 }} value={additionalNotes} onChange={e => setAdditionalNotes(e.target.value)} />
            </div>
          </div>

          <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8, background: 'var(--surface)' }}>
            <button onClick={onClose} style={{ fontSize: 13, color: 'var(--gray)', border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', padding: '6px 14px', background: '#fff', cursor: 'pointer' }}>Cancel</button>
            <button onClick={handleSubmit} disabled={saving} style={{ fontSize: 13, background: '#b91c1c', color: '#fff', border: 'none', borderRadius: 'var(--ew-r-md)', padding: '6px 16px', cursor: 'pointer', fontWeight: 600, opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Submitting…' : 'Submit Call Off'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Extend Modal ─────────────────────────────────────────────────────────────

export function ExtendModal({ item, onClose, onSaved }: { item: Equipment; onClose: () => void; onSaved: () => void }) {
  const qc = useQueryClient();
  const modalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalRef, { onEscape: onClose });
  const currentEnd = toDateInputValue(item.endDate);
  const [newEndDate, setNewEndDate]   = useState('');
  const [reason, setReason]           = useState('');
  const [updatedDuration, setUpdatedDuration] = useState(String(item.duration ?? ''));
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    if (!newEndDate) { setError('New end date is required.'); return; }
    if (currentEnd && newEndDate <= currentEnd) {
      setError('New end date must be after the current end date.'); return;
    }
    if (!reason.trim()) { setError('Reason for extension is required.'); return; }
    setSaving(true);
    try {
      await equipmentApi.extend(item.id, {
        newEndDate,
        reason: reason.trim(),
        updatedDuration: updatedDuration ? parseInt(updatedDuration, 10) : undefined,
      });
      qc.invalidateQueries({ queryKey: ['equipment'] });
      onSaved();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const inp: React.CSSProperties = { border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', padding: '6px 10px', fontSize: 13, width: '100%', fontFamily: 'inherit', background: '#fff', color: 'var(--text)' };
  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--gray)', marginBottom: 3, display: 'block' };
  const fw:  React.CSSProperties = { display: 'flex', flexDirection: 'column' };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div ref={modalRef} onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 'var(--ew-r-lg)', width: 'min(480px, 92vw)', maxHeight: '92vh', overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Extend Time on Site</div>
            <div style={{ fontSize: 12, color: 'var(--gray)', marginTop: 2 }}>
              {item.equipmentType ?? item.title}{item.project ? ` · #${item.project.jobNumber}` : ''}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close extend modal" style={{ fontSize: 18, color: 'var(--gray)', background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px' }}>✕</button>
        </div>

        {error && <div style={{ margin: '12px 20px 0', padding: '8px 12px', background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', fontSize: 12 }}>{error}</div>}

        <div style={{ padding: '16px 20px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--gray)', background: 'var(--surface)', padding: '8px 10px', borderRadius: 'var(--ew-r-md)', border: '1px solid var(--border)' }}>
            Current end date: <strong style={{ color: 'var(--text)' }}>{fmtDate(item.endDate)}</strong>
          </div>
          <div style={fw}>
            <label style={lbl}>New End Date <span style={{ color: '#b91c1c' }}>*</span></label>
            <input type="date" style={inp} min={currentEnd || undefined} value={newEndDate} onChange={e => setNewEndDate(e.target.value)} />
          </div>
          <div style={fw}>
            <label style={lbl}>Reason for Extension <span style={{ color: '#b91c1c' }}>*</span></label>
            <textarea style={{ ...inp, resize: 'vertical', minHeight: 70 }} value={reason} onChange={e => setReason(e.target.value)} placeholder="Why is the equipment needed longer?" />
          </div>
          <div style={fw}>
            <label style={lbl}>Updated Duration {item.billingCycle?.label ? `(${item.billingCycle.label})` : ''} (optional)</label>
            <input type="number" min={0} style={inp} value={updatedDuration} onChange={e => setUpdatedDuration(e.target.value)} />
          </div>
        </div>

        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8, background: 'var(--surface)' }}>
          <button onClick={onClose} style={{ fontSize: 13, color: 'var(--gray)', border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', padding: '6px 14px', background: '#fff', cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSubmit} disabled={saving} style={{ fontSize: 13, background: 'var(--ew)', color: '#fff', border: 'none', borderRadius: 'var(--ew-r-md)', padding: '6px 16px', cursor: 'pointer', fontWeight: 600, opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Submitting…' : 'Submit Extension'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── KPI Bar ──────────────────────────────────────────────────────────────────

function KpiBar({ data }: { data: Equipment[] }) {
  const counts = useMemo(() => {
    // Rental/Owned split counts active records only — Total includes
    // Returned/Canceled so it matches the full record count.
    let active = 0, rental = 0, owned = 0;
    for (const e of data) {
      if (isCompleted(e.status)) continue;
      active++;
      if (e.rentalType?.id === 1) rental++;
      else if (e.rentalType?.id === 2) owned++;
    }
    return { active, rental, owned, total: data.length };
  }, [data]);

  const card = (val: number, color: string, label: string) => (
    <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--ew-r-lg)', padding: '10px 18px', minWidth: 110 }}>
      <div style={{ fontSize: 24, fontWeight: 800, color, lineHeight: 1.15 }}>{val}</div>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.7px', color: 'var(--gray)' }}>{label}</div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
      {card(counts.active, '#0ea5e9', 'Active')}
      {card(counts.rental, '#d97706', 'Rental')}
      {card(counts.owned,  '#16a34a', 'Owned')}
      {card(counts.total,  '#6b7280', 'Total Records')}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function EquipmentPage() {
  const statuses = useEquipmentStatuses();
  const [search, setSearch]     = useState('');
  const [statusId, setStatusId] = useState<number | undefined>(undefined);
  const [sortKey, setSortKey]   = useState<SortKey>('status');
  const [showNew,  setShowNew]  = useState(false);
  const [sortDir, setSortDir]   = useState<SortDir>('asc');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showEdit, setShowEdit] = useState(false);
  const [editTarget, setEditTarget] = useState<Equipment | null>(null);
  const [callOffTarget, setCallOffTarget] = useState<Equipment | null>(null);
  const [extendTarget,  setExtendTarget]  = useState<Equipment | null>(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);

  const handleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const params = useMemo(() => ({
    ...(search   ? { search }   : {}),
    ...(statusId ? { statusId } : {}),
  }), [search, statusId]);

  const { data, isLoading, error } = useQuery<Equipment[]>({
    queryKey: ['equipment', params],
    queryFn: () => equipmentApi.list(params),
    staleTime: 30_000,
  });

  const { data: projects = [] } = useQuery<ProjectRef[]>({
    queryKey: ['equipment-project-options'],
    queryFn: () => equipmentApi.projectOptions(),
    staleTime: 300_000,
  });

  // Permission gating. This page previously rendered every write control to
  // everyone and let the API 403 on save — reading as a broken feature rather
  // than a missing permission. Every write route on equipment.controller.ts
  // requires 'equipment.edit'; Admin bypasses the guard server-side. Mirrors
  // the project Equipment tab, which already gated correctly.
  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: () => usersApi.me(),
    staleTime: 5 * 60 * 1000,
  });
  const canEdit = (me?.roles.includes('Admin') ?? false)
    || (me?.permissions.includes('equipment.edit') ?? false);
  // Who sees the rate and the files (#571). Since 2026-10-12 this is the same
  // rule as canEdit (equipment.edit or Admin); kept as its own value because
  // the two have differed before (equipment managers and Admin only, until
  // PMs were added) and may again.
  const rentalTerms = canSeeRentalTerms(me);

  // When the user picks a specific status, defer to that. Otherwise hide
  // Returned/Canceled by default unless "Include completed" is checked.
  const visibleData = useMemo(() => {
    if (!data) return [];
    if (statusId !== undefined || showCompleted) return data;
    return data.filter(d => !isCompleted(d.status));
  }, [data, statusId, showCompleted]);
  const hiddenCompletedCount = useMemo(() => {
    if (!data || statusId !== undefined || showCompleted) return 0;
    return data.filter(d => isCompleted(d.status)).length;
  }, [data, statusId, showCompleted]);

  const groups = useMemo(() => groupEquipment(visibleData, sortKey, sortDir), [visibleData, sortKey, sortDir]);
  const totalCount = useMemo(() => groups.reduce((n, g) => n + g.items.length, 0), [groups]);

  // Excel export: exactly what the table shows, in the order it shows it —
  // search, status filter and "Include completed" applied, grouped by job. The
  // Rate column only for equipment editors (see equipmentExport.ts).
  const handleExport = useCallback(() => {
    try {
      exportEquipmentToXlsx(groups.flatMap((g) => g.items), rentalTerms);
    } catch (e) {
      alert(`Export failed: ${(e as Error).message}`);
    }
  }, [groups, rentalTerms]);

  const COL_COUNT = 10;

  const selectStyle: React.CSSProperties = {
    border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', padding: '6px 10px',
    fontSize: 13, background: '#fff', color: 'var(--text)', cursor: 'pointer',
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h2 style={{ fontWeight: 700, fontSize: 18 }}>Equipment Requests</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: 'var(--gray)' }}>{isLoading ? 'Loading…' : `${totalCount} record${totalCount !== 1 ? 's' : ''}`}</span>
          <button onClick={() => setShowCalculator(true)}
            title="Estimate rental cost without creating a request"
            style={{ fontSize: 12, padding: '5px 14px', background: '#fff', color: 'var(--ew)', border: '1px solid var(--ew)', borderRadius: 'var(--ew-r-md)', cursor: 'pointer', fontWeight: 600 }}>
            Rate Calculator
          </button>
          {/* Equipment editors only (equipment.edit or Admin), like the other
              actions here. */}
          {canEdit && (
            <button onClick={handleExport} disabled={totalCount === 0}
              title={totalCount === 0
                ? 'Nothing to export — no requests match your filters'
                : `Export these ${totalCount} request${totalCount !== 1 ? 's' : ''} to Excel, as shown. Clear the filters and tick Include completed to export everything.`}
              style={{ fontSize: 12, padding: '5px 14px', background: '#fff', color: 'var(--ew)', border: '1px solid var(--ew)', borderRadius: 'var(--ew-r-md)', cursor: totalCount === 0 ? 'default' : 'pointer', fontWeight: 600, opacity: totalCount === 0 ? 0.6 : 1 }}>
              Export to Excel
            </button>
          )}
          {canEdit && (
            <button onClick={() => setShowNew(true)}
              style={{ fontSize: 12, padding: '5px 14px', background: 'var(--ew)', color: '#fff', border: 'none', borderRadius: 'var(--ew-r-md)', cursor: 'pointer', fontWeight: 600 }}>
              + New Request
            </button>
          )}
        </div>
      </div>

      <KpiBar data={data ?? []} />

      {/* Filter bar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 16, padding: '12px 16px', background: '#fff', borderRadius: 'var(--ew-r-lg)', border: '1px solid var(--border)' }}>
        <input type="search" placeholder="Search title, type, vendor, job # or project…" value={search} onChange={e => setSearch(e.target.value)}
          style={{ ...selectStyle, minWidth: 260, flexGrow: 1 }} />
        <select value={statusId ?? ''} onChange={e => setStatusId(e.target.value ? Number(e.target.value) : undefined)} style={selectStyle}>
          <option value="">All active</option>
          {/* Every status, retired ones included — you still need to be able to
              filter to legacy records. */}
          {statuses.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        {statusId === undefined && (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--gray)', cursor: 'pointer', userSelect: 'none', padding: '0 6px' }}>
            <input type="checkbox" checked={showCompleted} onChange={e => setShowCompleted(e.target.checked)} />
            Include completed
            {!showCompleted && hiddenCompletedCount > 0 && (
              <span style={{ background: 'var(--surface)', color: 'var(--gray)', border: '1px solid var(--border)', borderRadius: 'var(--ew-r-lg)', padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>
                {hiddenCompletedCount} hidden
              </span>
            )}
          </label>
        )}
      </div>

      {error && <div style={{ padding: 12, background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', marginBottom: 16, fontSize: 13 }}>{(error as Error).message}</div>}

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 'var(--ew-r-lg)', border: '1px solid var(--border)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '15%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '12%' }} />
            </colgroup>
            <thead>
              <tr>
                <Th label="Equipment"     sortKey="type"        current={sortKey} dir={sortDir} onSort={handleSort} />
                <Th label="Status"        sortKey="status"      current={sortKey} dir={sortDir} onSort={handleSort} />
                <Th label="Vendor"        sortKey="vendor"      current={sortKey} dir={sortDir} onSort={handleSort} />
                <Th label="Delivery"      sortKey="delivery"    current={sortKey} dir={sortDir} onSort={handleSort} />
                <Th label="End Date"      sortKey="endDate"     current={sortKey} dir={sortDir} onSort={handleSort} />
                <Th label="Rental/Owned"  sortKey="rentalType"  current={sortKey} dir={sortDir} onSort={handleSort} />
                <Th label="Owned Eq #"    sortKey="owned"       current={sortKey} dir={sortDir} onSort={handleSort} />
                <Th label="Requested By"  sortKey="requestedBy" current={sortKey} dir={sortDir} onSort={handleSort} />
                <Th label="Submitted"     sortKey="submitted"   current={sortKey} dir={sortDir} onSort={handleSort} />
                <th style={{ padding: '8px 8px', borderBottom: '2px solid var(--border)', background: 'var(--surface)' }} />
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={COL_COUNT} style={{ padding: 32, textAlign: 'center', color: 'var(--gray)' }}>Loading…</td></tr>}
              {!isLoading && groups.length === 0 && <tr><td colSpan={COL_COUNT} style={{ padding: 32, textAlign: 'center', color: 'var(--gray)' }}>No equipment records match your filters.</td></tr>}
              {groups.map(group => (
                <>
                  <tr key={`group-${group.key}`}>
                    <td colSpan={COL_COUNT} style={{
                      padding: '7px 12px',
                      // Soft neutral bar with green text + a thin green left accent,
                      // instead of a solid full-width green band (easier to scan).
                      background: '#EDEDEC',          // ew-n-100
                      color: 'var(--ew)',
                      fontWeight: 700,
                      fontSize: 12,
                      letterSpacing: '.3px',
                      borderTop: '1px solid var(--border)',
                      borderBottom: '1px solid var(--border)',
                      borderLeft: '3px solid var(--ew)',
                    }}>
                      {group.jobNumber ? `#${group.jobNumber} — ` : ''}{group.label}
                      <span style={{ fontWeight: 400, color: 'var(--gray)', marginLeft: 8 }}>({group.items.length})</span>
                    </td>
                  </tr>
                  {group.items.map((item, i) => {
                    const isExpanded = expandedId === item.id;
                    const rowBg = isExpanded ? 'var(--ew-dim)' : (i % 2 === 0 ? '#fff' : 'var(--surface)');
                    return (
                      <>
                        <tr key={item.id}
                          onClick={() => setExpandedId(id => id === item.id ? null : item.id)}
                          style={{ background: rowBg, borderBottom: isExpanded ? 'none' : '1px solid var(--border)', cursor: 'pointer' }}
                          onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = 'var(--ew-dim)'; }}
                          onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = rowBg; }}>
                          <td style={{ padding: '8px 10px 8px 20px', overflow: 'hidden' }}>
                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.equipmentType ?? '—'}</div>
                          </td>
                          <td style={{ padding: '8px 10px' }}><StatusBadge status={item.status} /></td>
                          <td style={{ padding: '8px 10px', color: 'var(--gray)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.vendor ?? '—'}</td>
                          <td style={{ padding: '8px 10px', color: 'var(--gray)', fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDate(item.deliveryDate)}</td>
                          {(() => {
                            const due = endDateStatus(item.status, item.endDate);
                            return (
                              <td
                                title={due?.label}
                                style={{
                                  padding: '8px 10px', fontSize: 12, whiteSpace: 'nowrap',
                                  color: (due && DUE_COLOR[due.tone]) ?? 'var(--gray)',
                                  fontWeight: due ? 600 : undefined,
                                }}
                              >
                                {fmtDate(item.endDate)}
                              </td>
                            );
                          })()}
                          <td style={{ padding: '8px 10px', color: 'var(--gray)', fontSize: 12, whiteSpace: 'nowrap' }}>{item.rentalType?.label ?? '—'}</td>
                          <td style={{ padding: '8px 10px', color: 'var(--gray)', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.ownedEquipmentNumber ?? '—'}</td>
                          <td style={{ padding: '8px 10px', color: 'var(--gray)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.requestedBy ?? '—'}</td>
                          <td style={{ padding: '8px 10px', color: 'var(--gray)', fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDateTime(item.createdAt)}</td>
                          <td style={{ padding: '8px 8px', textAlign: 'right' }}>
                            <div style={{ display: 'inline-flex', gap: 4, flexWrap: 'nowrap', justifyContent: 'flex-end' }}>
                              {canEdit && isOnSite(item.status) && (
                                <button onClick={e => { e.stopPropagation(); setCallOffTarget(item); }}
                                  title="Call off equipment"
                                  style={{ fontSize: 11, padding: '3px 7px', border: '1px solid #fca5a5', borderRadius: 'var(--ew-r-md)', background: '#fff', color: '#b91c1c', cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: 600 }}>
                                  Call Off
                                </button>
                              )}
                              {canEdit && (isOnSite(item.status) || isCalledOff(item.status)) && (
                                <button onClick={e => { e.stopPropagation(); setExtendTarget(item); }}
                                  title="Extend time on site"
                                  style={{ fontSize: 11, padding: '3px 7px', border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', background: '#fff', color: 'var(--ew)', cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: 600 }}>
                                  Extend
                                </button>
                              )}
                              {canEdit && (
                                <button onClick={e => { e.stopPropagation(); setEditTarget(item); setShowEdit(true); }}
                                  title="Edit equipment"
                                  style={{ fontSize: 11, padding: '3px 8px', border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', background: '#fff', color: 'var(--text)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                  Edit
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr key={`detail-${item.id}`} style={{ borderBottom: '2px solid var(--ew)' }}>
                            <td colSpan={COL_COUNT} style={{ padding: 0 }}>
                              <InlineDetail item={item} projects={projects} canEdit={canEdit} showRate={rentalTerms} onClose={() => setExpandedId(null)} onEdit={() => { setEditTarget(item); setShowEdit(true); setExpandedId(null); }} />
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </>
              ))}
            </tbody>
          </table>
      </div>

      {!isLoading && totalCount > 0 && (
        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--gray)', textAlign: 'right' }}>
          {totalCount} record{totalCount !== 1 ? 's' : ''} across {groups.length} project{groups.length !== 1 ? 's' : ''}{search || statusId ? ' (filtered)' : ''}
        </div>
      )}

      {showNew && (
        <EquipmentRequestForm
          projects={projects}
          onClose={() => setShowNew(false)}
          onSaved={() => setShowNew(false)}
        />
      )}

      {showEdit && (
        <EditDrawer
          item={editTarget}
          projects={projects}
          canSeeRentalTerms={rentalTerms}
          onClose={() => { setShowEdit(false); setEditTarget(null); }}
          onSaved={() => { setShowEdit(false); setEditTarget(null); }}
        />
      )}

      {callOffTarget && (
        <CallOffModal
          item={callOffTarget}
          projects={projects}
          onClose={() => setCallOffTarget(null)}
          onSaved={() => setCallOffTarget(null)}
        />
      )}

      {extendTarget && (
        <ExtendModal
          item={extendTarget}
          onClose={() => setExtendTarget(null)}
          onSaved={() => setExtendTarget(null)}
        />
      )}

      {showCalculator && <RateCalculatorModal onClose={() => setShowCalculator(false)} />}
    </div>
  );
}
